import React, { useState } from "react";
import { Alert, Nav, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useRouteLoaderData } from "react-router-dom";

import { useAppApi } from "@/App";
import { GameMicroReviewComplaintStatus } from "@/const";
import { useRequest } from "@components/AppHook";
import { ExportButton } from "@components/Common/Button";
import { DevForm, FormFieldGroup } from "@components/Forms/Fields";
import { RenderForm } from "@components/Forms/Render";
import { Awesome, DevModal } from "@components/Helpers";
import { CommonCard, ListingCard } from "@components/Pages";
import { url_reverse } from "@components/Utils";

const ReviewText = ({ text }) => {
    const length = 200;
    const textLength = text.length;
    const end = textLength > length ? "..." : "";
    return <div style={{ maxWidth: "600px" }}>{text.substring(0, length) + end}</div>;
};

const PublicationState = ({ review }) => {
    const { t } = useTranslation();

    return (
        <>
            {review?.is_published && <span className="badge badge-success">{t("Опубликовано")}</span>}
            {!review?.is_published && <span className="badge badge-warning">{t("Не опубликовано")}</span>}
            {review?.moder_comment !== "" && <span className="badge badge-secondary">{t("Ответил разработчик")}</span>}
        </>
    );
};

const MrTabs = ({ gameId }) => {
    const { t } = useTranslation();
    const params = useParams();
    const navigate = useNavigate();

    const handleSelect = (eventKey) => {
        navigate({
            pathname: url_reverse(`games:microreview:${eventKey}`, { game_id: gameId }),
        });
    };

    return (
        <Nav
            variant="tabs"
            activeKey={params.id || "list"}
            className="react-bootstrap-tabs-fix"
            onSelect={handleSelect}
        >
            <Nav.Item>
                <Nav.Link eventKey="list">{t("Список отзывов")}</Nav.Link>
            </Nav.Item>
            <Nav.Item>
                <Nav.Link eventKey="subscription_reviews">{t("Рассылки")}</Nav.Link>
            </Nav.Item>
            <Nav.Item>
                <Nav.Link eventKey="stats">{t("Статистика")}</Nav.Link>
            </Nav.Item>
        </Nav>
    );
};

const List = ({ header, gameId }) => {
    const { t } = useTranslation();
    const commonInfo = useRouteLoaderData("commonInfo");
    const { apiData, apiUrl, setApiReget } = useAppApi(url_reverse("api:game:microreview:list", { game_id: gameId }));
    const [complaint, setComplaint] = useState({});
    const { api_post } = useRequest();

    if (!apiData) return;

    const updateComplaint = (k, v) => {
        setComplaint((prev) => ({ ...prev, [k]: v }));
    };

    const hideComplaint = () => {
        setComplaint({});
    };

    const doComplaint = (ev) => {
        api_post(ev, apiUrl, {
            params: complaint,
            then: (resp) => {
                if (resp.status === "ok") {
                    hideComplaint();
                    setApiReget(true);
                }
            },
        });
    };

    const headerTools = commonInfo.account.is_super && apiData.object_list?.length > 0 && (
        <ExportButton size="sm" buildReport="microreviews" />
    );

    return (
        <ListingCard header={header} headerTools={headerTools}>
            <MrTabs gameId={gameId} />
            <Table responsive className="dmg-table">
                <thead>
                    <tr>
                        <th style={{ width: "3%" }} />
                        <th>{t("Автор")}</th>
                        <th>{t("Отзыв")}</th>
                        <th>{t("Язык")}</th>
                        <th>{t("Дата добавления")}</th>
                        <th>{t("Состояние")}</th>
                        <th style={{ width: "50px" }} />
                    </tr>
                </thead>
                <tbody>
                    {apiData.object_list?.map((review) => (
                        <tr key={review.id}>
                            <td>
                                <span className={`b-rating ${review.rating_class}`}>{review.rating_txt}</span>
                            </td>
                            <td>{review.author_ac_id}</td>
                            <td className="show-wrap-text">
                                <ReviewText text={review.text} />
                            </td>
                            <td>{review.lang}</td>
                            <td>{review.date_added}</td>
                            <td>
                                <PublicationState review={review} />
                            </td>
                            <td>
                                <Link
                                    to={url_reverse("games:microreview:edit", {
                                        game_id: gameId,
                                        microreview_id: review.id,
                                    })}
                                >
                                    <Awesome type={apiData.can_edit ? "pencil" : "eye"} />
                                </Link>
                                {review.complaint_status === GameMicroReviewComplaintStatus.NONE && (
                                    <>
                                        {" "}
                                        <Awesome
                                            type="ban"
                                            color="red"
                                            onClick={() => updateComplaint("id", review.id)}
                                        />
                                    </>
                                )}
                                {review.complaint_status === GameMicroReviewComplaintStatus.REQUEST && (
                                    <>
                                        {" "}
                                        <Awesome type="hourglass-o" tooltip={t("На рассмотрении")} />
                                    </>
                                )}
                                {review.complaint_status === GameMicroReviewComplaintStatus.APPROVE && (
                                    <>
                                        {" "}
                                        <Awesome
                                            type="thumbs-o-up"
                                            tooltip={t("Удовлетворена (отзыв скрыт)") + ": " + review.complaint_comment}
                                        />
                                    </>
                                )}
                                {review.complaint_status === GameMicroReviewComplaintStatus.REJECT && (
                                    <>
                                        {" "}
                                        <Awesome
                                            type="thumbs-o-down"
                                            tooltip={t("Отклонено") + ": " + review.complaint_comment}
                                        />
                                    </>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            <DevModal
                size="lg"
                show={complaint.id}
                onHide={hideComplaint}
                header={t("Жалоба")}
                body={
                    <FormFieldGroup label={t("Причина")} required={true}>
                        <DevForm.TextArea
                            name="reason"
                            value={complaint.reason}
                            required={true}
                            onChange={(ev) => updateComplaint("reason", ev.target.value)}
                        />
                    </FormFieldGroup>
                }
                footer={
                    <DevForm.Button variant="primary" type="submit" name="do_complaint" onClick={doComplaint}>
                        {t("Подтвердить")}
                    </DevForm.Button>
                }
            />
        </ListingCard>
    );
};

const Stats = ({ header, gameId }) => {
    const { t } = useTranslation();
    const { apiData } = useAppApi(url_reverse("api:game:microreview:stats", { game_id: gameId }));

    if (!apiData) return;

    return (
        <CommonCard header={header}>
            <MrTabs gameId={gameId} />
            <RenderForm submitButtonText={t("Применить")} />

            {apiData.object_list?.length === 0 && (
                <Alert variant={"danger"} className="mt-3">
                    {t("Нет данных за этот период")}
                </Alert>
            )}

            {apiData.object_list?.length > 0 && (
                <Table responsive className="dmg-table mt-2">
                    <thead>
                        <tr>
                            <th>{t("Дата")}</th>
                            <th>{t("Количество")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {apiData.object_list?.map((obj) => (
                            <tr key={obj.dt}>
                                <td>{obj.dt}</td>
                                <td>{obj.cnt}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </CommonCard>
    );
};

const Subscription = ({ header, gameId }) => {
    const { t } = useTranslation();
    const { apiData } = useAppApi(url_reverse("api:game:microreview:subscription_reviews", { game_id: gameId }));

    if (!apiData) return;
    return (
        <CommonCard header={header}>
            <MrTabs gameId={gameId} />
            <RenderForm submitButtonText={t("Сохранить")} />
        </CommonCard>
    );
};

const Edit = ({ id, header, gameId }) => {
    const { t } = useTranslation();
    const { apiData } = useAppApi(url_reverse("api:game:microreview:edit", { game_id: gameId, microreview_id: id }));

    if (!apiData) return;

    return (
        <CommonCard header={header}>
            {!apiData.can_edit && (
                <Alert variant="info">
                    {t("Для удаления или редактирования отзыва вы можете подать жалобу по")}{" "}
                    <a href="https://support.vkplay.ru/vkp/feedback/4731/newticket" target="_blank">
                        {t("ссылке.")}
                    </a>
                </Alert>
            )}
            <RenderForm backUrl={url_reverse("games:microreview:list", { game_id: gameId })} />
        </CommonCard>
    );
};

export default function MicroReviews(props) {
    const params = useParams();

    if (params.id) {
        if (params.id === "stats") {
            return <Stats {...props} />;
        }
        if (params.id === "subscription_reviews") {
            return <Subscription {...props} />;
        }
        props = { ...props, id: params.id };
        return <Edit {...props} />;
    }
    return <List {...props} />;
}
